import { Component, ChangeDetectionStrategy } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../store';
import { $siteFeature } from '../../store/sites/sites.selectors';
import { $flows, $flowsLoading } from '../../store/flows/flows.selectors';
import {
    createFlowAction,
    deleteDeviceAction,
    deleteFlowAction,
    updateDeviceNoteAction,
    updateFlowAction,
} from '../../store/flows/flows.actions';
import { FeatureState, Flow, SiteFeature } from '@myrtls/api-interfaces';
import { MatDialog } from '@angular/material/dialog';
import { CreateFlowModalComponent } from '../create-flow-modal/create-flow-modal.component';
import { SendFlowToDeviceModalComponent } from '../send-flow-to-device-modal/send-flow-to-device-modal.component';
import { EditDeviceNoteModalComponent } from '../edit-device-note-modal/edit-device-note-modal.component';
import { ConfirmModalComponent } from '../confirm-modal/confirm-modal.component';
import { combineLatest } from 'rxjs';
import { map, take } from 'rxjs/operators';

@Component({
    selector: 'myrtls-insights-flows',
    templateUrl: './flows.component.html',
    styleUrls: ['./flows.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class FlowsComponent {
    private readonly rawFlows$ = this.store.pipe(select($flows));
    flowsLoading$ = this.store.pipe(select($flowsLoading));
    flowsEditFeature$ = this.store.select($siteFeature(SiteFeature.FLOWS_EDIT));
    readonly FeatureState = FeatureState;

    flows$ = combineLatest([this.rawFlows$, this.flowsLoading$]).pipe(
        map(([flows, loading]) => (loading ? [] : (flows ?? []))),
    );

    devices$ = combineLatest([this.rawFlows$, this.flowsLoading$]).pipe(
        map(([flows, loading]) => {
            if (loading || flows === null) {
                return [];
            }

            const allDevices: {
                id: string;
                name?: string;
                model?: string;
                osVersion?: string;
                flowName: string;
                flowVersion: number;
                latestFlowVersion: number;
                operatingSystem?: string;
                note: string | null;
            }[] = [];

            for (const flow of flows) {
                for (const device of flow.devices) {
                    allDevices.push({
                        id: device.id,
                        name: device.info.name,
                        model: device.info.model,
                        osVersion: device.info.osVersion,
                        flowName: flow.name,
                        flowVersion: device.version,
                        latestFlowVersion: flow.version,
                        operatingSystem: device.info.operatingSystem,
                        note: device.note,
                    });
                }
            }
            return allDevices;
        }),
    );

    displayedColumns = [
        'name',
        'version',
        'last-modified',
        'devices',
        'actions',
    ];
    displayedDeviceColumns = [
        'id',
        'device-information',
        'flow-name',
        'flow-version',
        'note',
        'device-actions',
    ];

    constructor(
        private store: Store<AppState>,
        private dialog: MatDialog,
    ) {}

    createFlow() {
        this.dialog
            .open(CreateFlowModalComponent, {
                width: '500px',
            })
            .afterClosed()
            .pipe(take(1))
            .subscribe(flow => {
                if (flow) {
                    this.store.dispatch(createFlowAction({ flow }));
                }
            });
    }

    sendFlow(id: string) {
        this.dialog.open(SendFlowToDeviceModalComponent, {
            width: '500px',
            data: { flowId: id },
        });
    }

    editFlow(flow: Flow) {
        this.dialog
            .open(CreateFlowModalComponent, {
                width: '500px',
                data: { flow },
            })
            .afterClosed()
            .pipe(take(1))
            .subscribe(updatedFlow => {
                if (updatedFlow) {
                    this.store.dispatch(
                        updateFlowAction({
                            flow: { id: flow.id, ...updatedFlow },
                        }),
                    );
                }
            });
    }

    deleteFlow(id: string) {
        this.dialog
            .open(ConfirmModalComponent, {
                width: '500px',
                data: {
                    title: 'Delete Flow',
                    message:
                        'Are you sure you want to delete this flow?<br /><br /><span class="text-warning">This operation cannot be undone!</span>',
                    confirmLabel: 'Delete',
                    cancelLabel: 'Cancel',
                    confirmColor: 'warn',
                },
            })
            .afterClosed()
            .pipe(take(1))
            .subscribe(result => {
                if (result === true) {
                    this.store.dispatch(deleteFlowAction({ flowId: id }));
                }
            });
    }

    deleteDevice(deviceId: string) {
        this.dialog
            .open(ConfirmModalComponent, {
                width: '500px',
                data: {
                    title: 'Delete Device',
                    message:
                        'Are you sure you want to delete this device?<br /><br /><span class="text-warning">This operation cannot be undone!</span>',
                    confirmLabel: 'Delete',
                    cancelLabel: 'Cancel',
                    confirmColor: 'warn',
                },
            })
            .afterClosed()
            .pipe(take(1))
            .subscribe(result => {
                if (result === true) {
                    this.store.dispatch(deleteDeviceAction({ deviceId }));
                }
            });
    }

    editNote(device: {
        id: string;
        name?: string;
        model?: string;
        osVersion?: string;
        operatingSystem?: string;
        note: string | null;
    }) {
        this.dialog
            .open(EditDeviceNoteModalComponent, {
                width: '500px',
                data: {
                    deviceId: device.id,
                    note: device.note,
                    name: device.name,
                    model: device.model,
                    osVersion: device.osVersion,
                    operatingSystem: device.operatingSystem,
                },
            })
            .afterClosed()
            .pipe(take(1))
            .subscribe(note => {
                if (note !== undefined) {
                    this.store.dispatch(
                        updateDeviceNoteAction({ deviceId: device.id, note }),
                    );
                }
            });
    }

    downloadFlow(flow: Flow) {
        const data = {
            name: flow.name,
            version: flow.version,
            site: flow.siteId,
            json: flow.json,
        };
        const blob = new Blob([JSON.stringify(data, null, 2)], {
            type: 'application/json',
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `${flow.name}.json`);
        link.click();
        URL.revokeObjectURL(url);
    }

    serializeDevice(device: { id: string; version: number }) {
        return `(v${device.version}) ${device.id}`;
    }

    olderDevicesCount(
        devices: { id: string; version: number }[],
        lastVersion: number,
    ) {
        return devices.reduce((sum, device) => {
            if (device.version < lastVersion) {
                return sum + 1;
            } else {
                return sum;
            }
        }, 0);
    }

    formatOperatingSystem(operatingSystem: string) {
        switch (operatingSystem) {
            case 'ios':
                return 'iOS';
            case 'android':
                return 'Android';
            case 'windows':
                return 'Windows';
            case 'mac':
                return 'macOS';
            default:
                return 'Other';
        }
    }
}
